/**
 * About Page Component
 * Comprehensive information about the HeatCraftHP project
 * Includes project overview, objectives, approach, partners, and EU funding details
 * @module About
 */

import React from 'react';
import { Typography, Row, Col, Card, Timeline, Statistic, Divider, Tag } from 'antd';
import { 
  RocketOutlined, 
  TeamOutlined, 
  TrophyOutlined, 
  GlobalOutlined,
  ExperimentOutlined,
  BookOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  SolutionOutlined,
  PlayCircleOutlined,
  LinkedinOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../PagesWrapper';
import styles from './About.module.css';

const { Title, Paragraph, Text } = Typography;

/**
 * About Page - HeatCraftHP Project Information
 */
const About = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Project impact metrics by end of 2027
  const projectStats = [
    { title: t('about.stats.professionalsTrainedTitle'), value: '1,200', description: t('about.stats.professionalsTrainedDesc') },
    { title: t('about.stats.installationsTitle'), value: '20,000', description: t('about.stats.installationsDesc') },
    { title: t('about.stats.scopTitle'), value: '20%', description: t('about.stats.scopDesc') },
    { title: t('about.stats.energySavingsTitle'), value: '27.7', suffix: 'GWh/year', description: t('about.stats.energySavingsDesc') }
  ];

  // Core objectives of the project
  const objectives = [
    {
      icon: <BookOutlined />,
      title: t('about.objectiveItems.skillMapping.title'),
      description: t('about.objectiveItems.skillMapping.description')
    },
    {
      icon: <TeamOutlined />,
      title: t('about.objectiveItems.gapAnalysis.title'),
      description: t('about.objectiveItems.gapAnalysis.description')
    },
    {
      icon: <RocketOutlined />,
      title: t('about.objectiveItems.reskilling.title'),
      description: t('about.objectiveItems.reskilling.description')
    },
    {
      icon: <ExperimentOutlined />,
      title: t('about.objectiveItems.interactiveLearning.title'),
      description: t('about.objectiveItems.interactiveLearning.description')
    },
    {
      icon: <SolutionOutlined />,
      title: t('about.objectiveItems.trainTrainers.title'),
      description: t('about.objectiveItems.trainTrainers.description')
    }
  ];

  // Project consortium partners (with official website URLs)
  const partners = [
    { name: 'University of Twente', country: '🇳🇱 Netherlands', role: 'Coordinator', url: 'https://www.utwente.nl/en/' },
    { name: 'RWTH Aachen University', country: '🇩🇪 Germany', role: 'Research Partner', url: 'https://www.rwth-aachen.de/' },
    { name: 'European Heat Pump Association', country: '🇧🇪 Belgium', role: 'Dissemination', url: 'https://www.ehpa.org/' },
    { name: 'WIP Munich', country: '🇩🇪 Germany', role: 'Industry Partner', url: 'https://www.wip-munich.de/en/' },
    { name: 'OHKW Klimajobs', country: '🇩🇪 Germany', role: 'Workforce Development', url: 'https://ohkw.de/' },
    { name: 'Heat Pump Academy', country: '🇩🇪 Germany', role: 'Training Provider', url: 'https://www.heatpump-academy.de/' },
    { name: 'Centraal Register Techniek', country: '🇳🇱 Netherlands', role: 'Certification', url: 'https://www.centraalregistertechniek.nl/' },
    { name: 'IT University (ITU)', country: '🇦🇹 Austria', role: 'Innovation Partner', url: 'https://www.it-u.at/' }
  ];

  // Project timeline milestones
  const timeline = [
    {
      title: t('about.timelineLabels.q4_2024'),
      description: t('about.timelineItems.q4_2024'),
      status: 'completed'
    },
    {
      title: t('about.timelineLabels.q1_q2_2025'),
      description: t('about.timelineItems.q1_q2_2025'),
      status: 'completed'
    },
    {
      title: t('about.timelineLabels.q3_2025_q2_2026'),
      description: t('about.timelineItems.q3_2025_q2_2026'),
      status: 'ongoing'
    },
    {
      title: t('about.timelineLabels.q3_2026_q2_2027'),
      description: t('about.timelineItems.q3_2026_q2_2027'),
      status: 'pending'
    },
    {
      title: t('about.timelineLabels.now_2027'),
      description: t('about.timelineItems.now_2027'),
      isClickable: true
    }
  ];

  return (
    <PagesWrapper>
      <div className={styles.aboutContainer}>
        
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <Title level={1} className={styles.heroTitle}>
              {t('about.heroTitle')}
            </Title>
            <Paragraph className={styles.heroSubtitle}>
              {t('about.heroSubtitle')}
            </Paragraph>
            <a
              href="https://www.linkedin.com/company/heatcrafthp-project/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.heroLinkedInLink}
              aria-label="Open HeatCraftHP LinkedIn page in a new tab"
            >
              <LinkedinOutlined />
              <span>{t('about.linkedInCta', { defaultValue: 'Follow the project on LinkedIn' })}</span>
            </a>
          </div>
        </section>
        <Divider />
        {/* Challenge Section */}
        <section className={styles.challengeSection}>
          <div className={styles.sectionContent}>
            <Title level={2} className={styles.sectionTitle}>
              <GlobalOutlined /> {t('about.challengeTitle')}
            </Title>
            <Paragraph className={styles.bodyText}>
              {t('about.challengeText')}
            </Paragraph>
          </div>
        </section>
        <Divider />
        {/* Statistics Section */}
        <section className={styles.statsSection}>
          <div className={styles.sectionContent}>
            <Title level={2} className={styles.sectionTitle}>
              <BarChartOutlined /> {t('about.factsTitle')}
            </Title>
            <Row gutter={[24, 24]} justify="center">
            {projectStats.map((stat, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <Card className={styles.statCard} hoverable>
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    suffix={stat.suffix}
                    valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                  />
                  <Text type="secondary" className={styles.statDescription}>
                    {stat.description}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
          </div>
        </section>

        <Divider />

        {/* Objectives Section */}
        <section className={styles.objectivesSection}>
          <div className={styles.sectionContent}>
            <Title level={2} className={styles.sectionTitle}>
              <TrophyOutlined /> {t('about.objectivesTitle')}
            </Title>
            <Row gutter={[24, 24]}>
              {objectives.map((objective, index) => (
                <Col xs={24} md={12} key={index}>
                  <Card className={styles.objectiveCard}>
                    <div className={styles.objectiveIcon}>{objective.icon}</div>
                    <Title level={4} className={styles.objectiveTitle}>
                      {objective.title}
                    </Title>
                    <Paragraph className={styles.objectiveDescription}>
                      {objective.description}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        <Divider />

        {/* Approach Section */}
        <section className={styles.approachSection}>
          <div className={styles.sectionContent}>
            <Title level={2} className={styles.sectionTitle}>
              <ExperimentOutlined /> {t('about.approachTitle')}
            </Title>
            
            <Row gutter={[48, 24]}>
              <Col xs={24} md={12}>
                <div className={styles.approachCard}>
                  <Title level={3} className={styles.approachTitle}>
                    {t('about.workplaceLearning.title')}
                  </Title>
                  <Paragraph className={styles.bodyText}>
                    {t('about.workplaceLearning.description')}
                  </Paragraph>
                  <ul className={styles.featureList}>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.workplaceLearning.feature1')}</li>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.workplaceLearning.feature2')}</li>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.workplaceLearning.feature3')}</li>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.workplaceLearning.feature4')}</li>
                  </ul>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div className={styles.approachCard}>
                  <Title level={3} className={styles.approachTitle}>
                    {t('about.gameLearning.title')}
                  </Title>
                  <Paragraph className={styles.bodyText}>
                    {t('about.gameLearning.description')}
                  </Paragraph>
                  <ul className={styles.featureList}>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.gameLearning.feature1')}</li>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.gameLearning.feature2')}</li>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.gameLearning.feature3')}</li>
                    <li><CheckCircleOutlined className={styles.checkIcon} /> {t('about.gameLearning.feature4')}</li>
                  </ul>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <Divider />

        {/* Timeline Section */}
        <section className={styles.timelineSection}>
          <div className={styles.sectionContent}>
            <Title level={2} className={styles.sectionTitle}>
              {t('about.timelineTitle')}
            </Title>
            <div className={styles.timelineWrapper}>
              <Timeline
                mode="left"
                items={timeline.map(item => ({
                  label: item.title,
                  children: (
                    <div 
                      onClick={item.isClickable ? () => navigate('/contact') : undefined}
                      style={{ 
                        cursor: item.isClickable ? 'pointer' : 'default',
                        display: 'inline-block'
                      }}
                      className={item.isClickable ? styles.clickableTimelineItem : ''}
                    >
                      <Text strong style={{ color: item.isClickable ? '#1890ff' : 'inherit' }}>
                        {item.isClickable && <PlayCircleOutlined style={{ marginRight: 8 }} />}
                        {item.description}
                      </Text>
                      {item.status && (
                        <>
                          <br />
                          <Tag color={
                            item.status === 'completed' ? 'green' : 
                            item.status === 'ongoing' ? 'blue' : 'default'
                          }>
                            {item.status.toUpperCase()}
                          </Tag>
                        </>
                      )}
                    </div>
                  ),
                  color: item.status === 'completed' ? 'green' : 
                         item.status === 'ongoing' ? 'blue' : 
                         item.isClickable ? 'blue' : 'gray'
                }))}
              />
            </div>
          </div>
        </section>

        <Divider />

        {/* Partners Section */}
        <section className={styles.partnersSection}>
          <div className={styles.sectionContent}>
            <Title level={2} className={styles.sectionTitle}>
              <TeamOutlined /> {t('about.partnersTitle')}
            </Title>
            <Paragraph className={styles.bodyText}>
              {t('about.partnersText')}
            </Paragraph>
            <Row gutter={[16, 16]} className={styles.partnersGrid}>
              {partners.map((partner, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card
                    className={`${styles.partnerCard} ${styles.partnerCardClickable}`}
                    hoverable
                    onClick={() => window.open(partner.url, '_blank', 'noopener,noreferrer')}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        window.open(partner.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    aria-label={`Open ${partner.name} website in a new tab`}
                  >
                    <div className={styles.partnerContent}>
                      <Text strong className={styles.partnerName}>{partner.name}</Text>
                      <Text type="secondary" className={styles.partnerCountry}>{partner.country}</Text>
                      <Tag color="blue" className={styles.partnerRole}>{partner.role}</Tag>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        <Divider />

        {/* Funding Section */}
        <section className={styles.fundingSection}>
          <Card className={styles.fundingCard}>
            <Title level={3} className={styles.fundingTitle}>
              {t('about.fundingTitle')}
            </Title>
            <Paragraph className={styles.bodyText}>
              {t('about.fundingText')}
              <Text strong> No 101167691</Text>. {t('about.fundingDetails')}
            </Paragraph>
            <Paragraph className={styles.bodyText}>
              <Text type="secondary">
                {t('about.projectId')}
              </Text>
            </Paragraph>
          </Card>
        </section>

      </div>
    </PagesWrapper>
  );
};

export default About;
